import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const WEB3FORMS_KEY = import.meta.env.VITE_WEB3FORMS_KEY || "";

export default function ContactSection() {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error" | "missingKey">("idle");
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = (formData.get("name") as string || "").trim();
    const email = (formData.get("email") as string || "").trim();
    const message = (formData.get("message") as string || "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
      return;
    }

    if (!WEB3FORMS_KEY) {
      setStatus("missingKey");
      setTimeout(() => setStatus("idle"), 5000);
      return;
    }

    setStatus("submitting");

    const payload = {
      access_key: WEB3FORMS_KEY,
      name,
      email,
      phone: (formData.get("phone") as string || "").trim(),
      message,
      subject: "New Contact Form Submission - AX Axis",
      from_name: "Website Contact Form",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({ success: false }));

      if (res.ok && data.success) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }

    setTimeout(() => setStatus("idle"), 4000);
  };

  return (
    <section id="contact" ref={sectionRef} className="bg-ax-dark py-20 md:py-28">
      <div className="px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <div className="animate-in opacity-0 translate-y-6 transition-all duration-700">
            <span className="text-xs text-ax-silver-dark tracking-[0.3em] uppercase block mb-4">
              {t("contact.label")}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-4">
              {t("contact.title")}
            </h2>
            <p className="text-ax-silver-dark text-sm md:text-base max-w-lg mx-auto">
              {t("contact.description")}
            </p>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left: Contact Info */}
          <div className="lg:w-2/5 animate-in opacity-0 translate-y-6 transition-all duration-700 delay-100">
            <h3 className="font-serif text-xl md:text-2xl text-white font-bold mb-2">
              {t("contact.infoTitle")}
            </h3>
            <p className="text-sm text-ax-silver-dark mb-8">
              {t("contact.reachUs")}
            </p>

            <div className="space-y-6">
              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 shrink-0">
                  <i className="ri-map-pin-line text-ax-red text-lg" />
                </div>
                <div>
                  <p className="text-[11px] text-ax-silver-dark uppercase tracking-[0.2em] mb-1">
                    {t("contact.addressTitle")}
                  </p>
                  <p className="text-sm text-white leading-relaxed">
                    {t("footer.addressLine1")}
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 shrink-0">
                  <i className="ri-mail-line text-ax-red text-lg" />
                </div>
                <div>
                  <p className="text-[11px] text-ax-silver-dark uppercase tracking-[0.2em] mb-1">
                    {t("contact.emailTitle")}
                  </p>
                  <a
                    href="mailto:contact@axaxis.com"
                    className="text-sm text-white hover:text-ax-red transition-colors"
                  >
                    contact@axaxis.com
                  </a>
                </div>
              </div>
            </div>

            {/* Decorative line */}
            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="text-xs text-ax-silver-dark leading-relaxed">
                {t("footer.tagline")}
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:w-3/5">
            <form
              id="contact_form"
              onSubmit={handleSubmit}
              className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-200 bg-white/5 border border-white/10 rounded-2xl p-6 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Name */}
                <div>
                  <label htmlFor="contact-name" className="block text-xs text-ax-silver mb-2">
                    {t("contact.nameLabel")} <span className="text-ax-red">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-ax-silver-dark/60 focus:outline-none focus:border-ax-red/50 transition-colors"
                    placeholder={t("contact.namePlaceholder")}
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="contact-email" className="block text-xs text-ax-silver mb-2">
                    {t("contact.emailLabel")} <span className="text-ax-red">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-ax-silver-dark/60 focus:outline-none focus:border-ax-red/50 transition-colors"
                    placeholder={t("contact.emailPlaceholder")}
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="mb-6">
                <label htmlFor="contact-phone" className="block text-xs text-ax-silver mb-2">
                  {t("contact.phoneLabel")}
                </label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-ax-silver-dark/60 focus:outline-none focus:border-ax-red/50 transition-colors"
                  placeholder={t("contact.phonePlaceholder")}
                />
              </div>

              {/* Message */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="contact-message" className="block text-xs text-ax-silver">
                    {t("contact.messageLabel")} <span className="text-ax-red">*</span>
                  </label>
                  <span className="text-[10px] text-ax-silver-dark">
                    {charCount}/500
                  </span>
                </div>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-ax-silver-dark/60 focus:outline-none focus:border-ax-red/50 transition-colors resize-none"
                  placeholder={t("contact.messagePlaceholder")}
                />
              </div>

              {/* Submit */}
              <div className="flex items-center gap-4 flex-wrap">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 bg-ax-red hover:bg-ax-red-dark text-white text-sm font-medium px-8 py-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center" />
                      {t("contact.submitting")}
                    </>
                  ) : (
                    <>
                      {t("contact.submit")}
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </span>
                    </>
                  )}
                </button>

                {status === "success" && (
                  <span className="text-sm text-emerald-400 flex items-center gap-1.5">
                    <i className="ri-check-line" />
                    {t("contact.success")}
                  </span>
                )}
                {status === "error" && (
                  <span className="text-sm text-ax-red flex items-center gap-1.5">
                    <i className="ri-error-warning-line" />
                    {t("contact.error")}
                  </span>
                )}
                {status === "missingKey" && (
                  <span className="text-sm text-amber-400 flex items-center gap-1.5">
                    <i className="ri-error-warning-line" />
                    {t("contact.web3MissingKey")}
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}